import { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { useCreatePriceInvisibilityRuleMutation, TotadminPermission } from 'data/__generated__';
import { organizationSource, productSource } from 'data/entity-sources/sources';

import { TotemInput } from 'components/TotemInput';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { UltraSelect } from 'components/UltraSelect';

export function PriceInvisibilityRulePopup({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
}) {
    const navigate = useNavigate();
    const [name, setName] = useState<string>('');
    const [organizationIds, setOrganizationIds] = useState<string[]>([]);
    const [productIds, setProductIds] = useState<string[]>([]);

    const [createPriceInvisibilityRule, { loading }] = useCreatePriceInvisibilityRuleMutation({
        refetchQueries: ['GET_PRICE_INVISIBILITY_RULES'],
    });

    const reset = () => {
        setName('');
        setOrganizationIds([]);
        setProductIds([]);
    };

    const handleClose = () => {
        setIsOpen(false);
        reset();
    };

    const handleSubmit = async () => {
        try {
            const result = await createPriceInvisibilityRule({
                variables: { input: { name, organizationIds, productIds } },
            });
            const newRuleId = result.data?.createPriceInvisibilityRule._id;

            if (!newRuleId) {
                toast.error('Une erreur est survenue lors de la création de la règle');
                return;
            }

            toast.success('Règle créée');
            setIsOpen(false);
            reset();
            navigate(`/price-invisibility-rule/${newRuleId}`);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Une erreur est survenue');
        }
    };

    return (
        <TotemPopup title="Créer une règle" isOpen={isOpen} setIsOpen={handleClose} contentOverflow="visible">
            <Fields>
                <TotemInput
                    label="Nom de la règle"
                    sublabel="Pourquoi ces prix sont masqués (ex. « Consommables café — Groupe Avril »)"
                    onChange={setName}
                    placeholder="Nom"
                    value={name}
                    autoFocus
                />
                <UltraSelect
                    source={organizationSource}
                    label="Organisations"
                    sublabel="Clients pour qui ces prix seront masqués"
                    multiple
                    value={organizationIds}
                    onChange={setOrganizationIds}
                />
                <UltraSelect
                    source={productSource}
                    label="Produits"
                    sublabel="Produits dont le prix ne sera pas affiché"
                    multiple
                    value={productIds}
                    onChange={setProductIds}
                />
                <TotemPrimaryLoadingButton
                    onClick={handleSubmit}
                    disabled={!name || !organizationIds.length || !productIds.length}
                    loading={loading}
                    useIcon={false}
                    label="Créer la règle"
                    requiredPermissions={[TotadminPermission.PriceInvisibilityRulesManage]}
                />
            </Fields>
        </TotemPopup>
    );
}

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 15px;

    > :not(:first-child) {
        margin-top: 15px;
    }
`;
